import React, { useState, useMemo, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { Box, IconButton, Input, InputAdornment, Tooltip, Select, MenuItem } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';

// Filtro de período con rango opcional (Mantis #254) y selector desplegable (Mantis #297).
//
// Contrato del valor que viaja al backend:
//   'YYYY-MM'                → período puntual (comportamiento previo)
//   ['YYYY-MM', 'YYYY-MM']   → rango cerrado
// Con "Hasta" vacío el valor sigue siendo un texto suelto, así que las pantallas
// que sólo usan período puntual no cambian en nada.
//
// Si se pasa `periodOptions` (lista de 'YYYY-MM'), se renderiza como dos comboboxes
// (Desde / Hasta); sin lista, sigue siendo dos inputs de texto (resto de las vistas).

const PERIODO_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

const PeriodoFilter = ({ column, header, table, filters, periodOptions = [] }) => {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const usaDropdown = Array.isArray(periodOptions) && periodOptions.length > 0;

  // Sincroniza con el filtro guardado en el padre (puntual o rango).
  useEffect(() => {
    const elem = filters.find(filter => filter.id === column.id);
    const valor = elem ? elem.value : '';
    const [d, h] = Array.isArray(valor)
      ? [valor[0] ?? '', valor[1] ?? '']
      : [valor ?? '', ''];

    setDesde(prev => (prev === d ? prev : d));
    setHasta(prev => (prev === h ? prev : h));
  }, [filters, column.id]);

  // Núcleo del contrato del filtro (sin debounce): se comparte entre texto y dropdown.
  const commitFiltro = useCallback((d, h) => {
    const dOk = PERIODO_RE.test(d);
    const hOk = PERIODO_RE.test(h);

    // Los dos vacíos: se limpia el filtro.
    if (!d && !h) {
      column.setFilterValue('');
      return;
    }
    // Los dos completos: rango (se invierte si vino al revés).
    if (dOk && hOk) {
      column.setFilterValue(d <= h ? [d, h] : [h, d]);
      return;
    }
    // Uno solo completo y el otro vacío: ese período puntual.
    if (dOk && !h) {
      column.setFilterValue(d);
      return;
    }
    if (hOk && !d) {
      column.setFilterValue(h);
      return;
    }
    // Alguno a medio escribir: no se dispara consulta.
  }, [column]);

  // Los inputs de texto aplican con espera (evita disparar consulta a medio escribir).
  const aplicarFiltro = useMemo(() => debounce(commitFiltro, 500), [commitFiltro]);

  const handleDesde = (event) => {
    const value = event.target.value;
    setDesde(value);
    aplicarFiltro(value, hasta);
  };

  const handleHasta = (event) => {
    const value = event.target.value;
    setHasta(value);
    aplicarFiltro(desde, value);
  };

  // El dropdown es una selección discreta: aplica de inmediato, sin debounce.
  const handleDesdeSelect = (event) => {
    const value = event.target.value;
    setDesde(value);
    commitFiltro(value, hasta);
  };

  const handleHastaSelect = (event) => {
    const value = event.target.value;
    setHasta(value);
    commitFiltro(desde, value);
  };

  const handleClearInput = () => {
    setDesde('');
    setHasta('');
    aplicarFiltro.cancel?.();
    commitFiltro('', '');
  };

  if (usaDropdown) {
    // Si el valor actual no está en la lista (ej. default), lo agregamos para que el
    // Select no quede fuera de rango.
    const opcionesDesde = desde && !periodOptions.includes(desde)
      ? [desde, ...periodOptions] : periodOptions;
    const opcionesHasta = hasta && !periodOptions.includes(hasta)
      ? [hasta, ...periodOptions] : periodOptions;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '210px' }}>
        <Tooltip arrow title="Período (desde)">
          <Select
            value={desde}
            onChange={handleDesdeSelect}
            displayEmpty
            variant="standard"
            fullWidth
            sx={{ fontSize: '0.85rem' }}
            renderValue={(v) => v || 'Periodo'}
          >
            <MenuItem value=""><em>Periodo</em></MenuItem>
            {opcionesDesde.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </Tooltip>
        <Tooltip arrow title="Hasta (opcional): elegilo para filtrar un rango de períodos">
          <Select
            value={hasta}
            onChange={handleHastaSelect}
            displayEmpty
            variant="standard"
            fullWidth
            sx={{ fontSize: '0.85rem' }}
            renderValue={(v) => v || 'Hasta'}
          >
            <MenuItem value=""><em>Hasta</em></MenuItem>
            {opcionesHasta.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </Tooltip>
        <InputAdornment position='end'>
          <IconButton
            size="small"
            disabled={!desde && !hasta}
            aria-label="limpiar filtro de período"
            onClick={handleClearInput}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '170px' }}>
      <Tooltip arrow title="Período (AAAA-MM)">
        <Input
          id="standard-periodo-filter"
          type={'text'}
          placeholder='Periodo'
          value={desde}
          onChange={handleDesde}
          fullWidth
        />
      </Tooltip>
      <Tooltip arrow title="Hasta (opcional): completalo para filtrar un rango de períodos">
        <Input
          id="standard-periodo-filter-hasta"
          type={'text'}
          placeholder='Hasta'
          value={hasta}
          onChange={handleHasta}
          fullWidth
        />
      </Tooltip>
      <InputAdornment position='end'>
        <IconButton
          size="small"
          disabled={!desde && !hasta}
          aria-label="limpiar filtro de período"
          onClick={handleClearInput}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    </Box>
  );
};
export default PeriodoFilter;
